import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest, getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";
import { addToLoader } from "../loader/loaderSlice";
import { checkCurrentLanguage, checkIfIsRoyalInternational } from "../../utilities/helper";

const userDetails = getUserDetails();
const initialState = {
    value: checkIfIsRoyalInternational(userDetails?.user_details?.travel_agency_uid)
        ? {
              first_name: "",
              last_name: "",
              year: "",
              month: [],
              day: [],
              customer_type: "",
              language: "",
              ship_uid: [],
          }
        : {
              customer_name: "",
              year: "",
              month: [],
              day: [],
              customer_type: "",
              language: "",
              ship_uid: [],
          },
    allShips: [],
    results: [],
    availableMonths: [],
    availableDays: [],
    availableYears: [],
    nordicLanguage: "",
    requireSupport: "",
    selectedShipNames: [],
    status: "idle",
};

export const getItinerayShips = createAsyncThunk("allShips/get", async (_, thunkAPI) => {
    thunkAPI.dispatch(addToLoader(true));
    const response = await apiRequest({
        payload: {
            data: {
                user_uid: getUserDetails().user_details.uid,
                language: window.currentLanguage.toString(),
            },
        },
        requestType: requestType.getItinerayShips,
    });
    thunkAPI.dispatch(addToLoader(false));
    return response.data.data;
});

export const getAvailableMonths = createAsyncThunk(
    "allMonths/get",
    async ({ year, shipUid }, thunkAPI) => {
        thunkAPI.dispatch(addToLoader(true));
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: getUserDetails().user_details.uid,
                    year: year,
                    ship_uid: shipUid,
                    language: window.currentLanguage.toString(),
                },
            },
            requestType: requestType.getAvailableMonths,
        });
        thunkAPI.dispatch(addToLoader(false));
        return response.data.data;
    }
);

export const getAvailableDays = createAsyncThunk(
    "allDays/get",
    async ({ year, month, shipUid }, thunkAPI) => {
        thunkAPI.dispatch(addToLoader(true));
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: getUserDetails().user_details.uid,
                    year: year,
                    month: month,
                    ship_uid: shipUid,
                    language: window.currentLanguage.toString(),
                },
            },
            requestType: requestType.getAvailableDays,
        });
        thunkAPI.dispatch(addToLoader(false));
        return response.data.data;
    }
);

export const getAvailableItinerayYears = createAsyncThunk(
    "availableItineraryYears/get",
    async ({ shipUid }, thunkAPI) => {
        thunkAPI.dispatch(addToLoader(true));
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: getUserDetails().user_details.uid,
                    ship_uid: shipUid,
                    language: window.currentLanguage.toString(),
                },
            },
            requestType: requestType.getAvailableItineraryYears,
        });
        thunkAPI.dispatch(addToLoader(false));
        return response.data.data;
    }
);

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const incrementAsync = createAsyncThunk("createYourHoliday/fetchCount", async (amount) => {
    // The value we return becomes the `fulfilled` action payload
});

export const createYourHolidaySlice = createSlice({
    name: "createYourHoliday",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        addCreateYourHolidayData: (state, action) => {
            state.value = action.payload;
        },
        addCreateYourHolidayResults: (state, action) => {
            state.results = action.payload;
        },
        addNordicLanguage: (state, action) => {
            state.nordicLanguage = action.payload;
        },
        addAllShips: (state, action) => {
            state.allShips = action.payload;
        },
        addRequireSupport: (state, action) => {
            state.requireSupport = action.payload;
        },
        addSelectedShipNames: (state, action) => {
            state.selectedShipNames = action.payload;
        },
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(getItinerayShips.pending, (state) => {
                state.loading = true;
            })
            .addCase(getItinerayShips.fulfilled, (state, { payload }) => {
                // When the API call is successful and we get some data,the data becomes the `fulfilled` action payload
                state.loading = false;
                state.allShips = payload;
            })
            .addCase(getItinerayShips.rejected, (state) => {
                state.loading = false;
                state.error = true;
            })

            .addCase(getAvailableMonths.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAvailableMonths.fulfilled, (state, { payload }) => {
                // When the API call is successful and we get some data,the data becomes the `fulfilled` action payload
                state.loading = false;
                state.availableMonths = payload?.available_itinerary_months;
            })
            .addCase(getAvailableMonths.rejected, (state) => {
                state.loading = false;
                state.error = true;
            })

            .addCase(getAvailableDays.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAvailableDays.fulfilled, (state, { payload }) => {
                // When the API call is successful and we get some data,the data becomes the `fulfilled` action payload
                state.loading = false;
                state.availableDays = payload?.available_itinerary_days;
            })
            .addCase(getAvailableDays.rejected, (state) => {
                state.loading = false;
                state.error = true;
            })

            .addCase(getAvailableItinerayYears.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAvailableItinerayYears.fulfilled, (state, { payload }) => {
                // When the API call is successful and we get some data,the data becomes the `fulfilled` action payload
                state.loading = false;
                state.availableYears = payload?.available_itinerary_years;
            })
            .addCase(getAvailableItinerayYears.rejected, (state) => {
                state.loading = false;
                state.error = true;
            });
    },
});

export const {
    addCreateYourHolidayData,
    addCreateYourHolidayResults,
    addNordicLanguage,
    addAllShips,
    addRequireSupport,
    addSelectedShipNames,
} = createYourHolidaySlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.createYourHoliday.value)`
export const selectCreateYourHoliday = (state) => state.createYourHoliday.value;
export const selectCreateYourHolidayResults = (state) => state.createYourHoliday.results;
export const selectNordicLanguage = (state) => state.createYourHoliday.nordicLanguage;
export const selectAllShips = (state) => state.createYourHoliday.allShips;
export const selectAvailableMonths = (state) => state.createYourHoliday.availableMonths;
export const selectAvailableDays = (state) => state.createYourHoliday.availableDays;
export const selectAvailableYears = (state) => state.createYourHoliday.availableYears;
export const selectRequireSupport = (state) => state.createYourHoliday.requireSupport;
export const selectSelectedShipNames = (state) => state.createYourHoliday.selectedShipNames;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const clearCreateYourHolidayData = () => (dispatch, getState) => {
    localStorage.removeItem("createYourHoliday");
    dispatch(
        addCreateYourHolidayData(
            checkIfIsRoyalInternational(userDetails?.user_details?.travel_agency_uid)
                ? {
                      first_name: "",
                      last_name: "",
                      year: "",
                      month: [],
                      day: [],
                      customer_type: "",
                      language: "",
                      ship_uid: [],
                  }
                : {
                      customer_name: "",
                      year: "",
                      month: [],
                      day: [],
                      customer_type: "",
                      language: "",
                      ship_uid: [],
                  }
        )
    );
    dispatch(addNordicLanguage(""));
    dispatch(addRequireSupport(""));
    dispatch(addSelectedShipNames([]));
};

export default createYourHolidaySlice.reducer;
